import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { UsePdfPreviewLayoutOptions } from "./types";
import { PDF_PREVIEW_DEFAULT_WIDTH } from "./consts";

export const usePdfPreviewLayout = ({
  defaultWidth = PDF_PREVIEW_DEFAULT_WIDTH,
  footerHeight,
  headerHeight = 0,
  debounceMs,
}: UsePdfPreviewLayoutOptions) => {
  const [resizeWidth, setResizeWidth] = useState(defaultWidth);
  const [containerWidth, setContainerWidth] = useState(defaultWidth);
  const [windowHeight, setWindowHeight] = useState(
    window.innerHeight - footerHeight - headerHeight,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const handleResize = () =>
      setWindowHeight(window.innerHeight - footerHeight - headerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [footerHeight, headerHeight]);

  // Read the container's content width synchronously before the first paint
  // so PdfDocument always receives the correct width on its initial render,
  // avoiding a post-load canvas re-render that causes visible glitching.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const style = getComputedStyle(el);
    const hPad = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    setContainerWidth(el.clientWidth - hPad);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    let observer: ResizeObserver | undefined;
    if (el) {
      observer = new ResizeObserver(([entry]) => {
        if (debounceMs) {
          clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            setContainerWidth(entry.contentRect.width);
          }, debounceMs);
        } else {
          setContainerWidth(entry.contentRect.width);
        }
      });
      observer.observe(el);
    }
    return () => {
      observer?.disconnect();
      clearTimeout(debounceRef.current);
    };
  }, [debounceMs]);

  const onResize = (_: unknown, { size }: { size: { width: number } }) =>
    setResizeWidth(size.width);

  return { resizeWidth, containerWidth, windowHeight, containerRef, onResize };
};
